import { useEffect } from "react";
import { generatePath, useNavigate, useParams } from "react-router";
import { NofilesIllustration } from "vega-illustrations";

import { FeatureFlag } from "@vega-api/extended/auth/useFeatureFlag";
import { useGetInsightsLimitedPartner } from "@vega-api/generated/insights/insights";
import { InsightsDealActivityStatusEnum } from "@vega-api/generated/types/insightsDealActivityStatusEnum";
import { type LimitedPartnerDealActivity } from "@vega-api/generated/types/limitedPartnerDealActivity";
import { type LimitedPartnerFundBreakdown } from "@vega-api/generated/types/limitedPartnerFundBreakdown";
import { type LimitedPartnerFundBreakdownTotals } from "@vega-api/generated/types/limitedPartnerFundBreakdownTotals";
import { EmptyCard } from "@vega-core/components/Cards/EmptyCard/EmptyCard";
import { m } from "@vega-core/paraglide/messages";
import { isGPCoInvestPortal } from "@vega-core/utils/global";
import { useFeatureFlagGuard } from "@vega-modules/hooks/useFeatureFlagGuard";
import { CoInvestmentFullPaths, CommonPaths } from "@vega-modules/paths";

import { CoInvestmentPageHeader } from "../components/CoInvestmentPageHeader/CoInvestmentPageHeader";
import { DealActivityList } from "../components/DealActivityList/DealActivityList";
import { FundBreakdownTable } from "../components/FundBreakdownTable/FundBreakdownTable";
import { InsightsChartCards } from "../components/InsightsChartCards/InsightsChartCards";
import { KpiMetricCards } from "../components/KpiMetricCards/KpiMetricCards";
import { LpVisibilitySection } from "../components/LpVisibilitySection/LpVisibilitySection";
import { useInsightsLiveRefresh } from "../hooks/useInsightsLiveRefresh";
import {
  type ChartInsightTrackingMetric,
  type ScalarInsightTrackingMetric,
  splitInsightMetrics,
} from "../utils/metricGuards";

import { ReportSectionMessage } from "./components/ReportSectionMessage";

type LimitedPartnerProfileSelection = {
  limitedPartnerName: string;
  kpiMetrics: ScalarInsightTrackingMetric[];
  chartMetrics: ChartInsightTrackingMetric[];
  fundBreakdown: LimitedPartnerFundBreakdown[];
  fundBreakdownTotals?: LimitedPartnerFundBreakdownTotals;
  deals: LimitedPartnerDealActivity[];
};

const EMPTY_PROFILE: LimitedPartnerProfileSelection = {
  limitedPartnerName: "",
  kpiMetrics: [],
  chartMetrics: [],
  fundBreakdown: [],
  deals: [],
};

export const InsightsLimitedPartnerPage = () => {
  useFeatureFlagGuard(
    FeatureFlag.COINVEST_INSIGHTS_VD_26155,
    // Only redirect to home when not in GP co-invest portal
    isGPCoInvestPortal ? undefined : CommonPaths.Home,
  );

  useInsightsLiveRefresh();

  const navigate = useNavigate();
  const { limitedPartnerId = "" } = useParams<{ limitedPartnerId: string }>();

  useEffect(() => {
    // The window keeps the insights page's scroll offset (e.g. scrolled down
    // to the organisations table) across the client-side route change.
    window.scrollTo(0, 0);
  }, []);

  const {
    data: profile,
    isPending,
    isError,
    refetch,
  } = useGetInsightsLimitedPartner<LimitedPartnerProfileSelection | undefined>(
    limitedPartnerId,
    {
      query: {
        select: (response): LimitedPartnerProfileSelection => ({
          limitedPartnerName: response.data.limitedPartner.limitedPartnerName,
          ...splitInsightMetrics(response.data),
          fundBreakdown: response.data.fundBreakdown,
          fundBreakdownTotals: response.data.fundBreakdownTotals,
          deals: response.data.deals,
        }),
      },
    },
  );

  const {
    limitedPartnerName,
    kpiMetrics,
    chartMetrics,
    fundBreakdown,
    fundBreakdownTotals,
    deals,
  } = profile ?? EMPTY_PROFILE;

  const renderContent = () => {
    if (isError) {
      return (
        <ReportSectionMessage
          dataTestId="lp-profile-error"
          message={m.common_error_somethingWentWrong()}
          onRetry={refetch}
        />
      );
    }

    if (
      !isPending &&
      kpiMetrics.length === 0 &&
      chartMetrics.length === 0 &&
      fundBreakdown.length === 0 &&
      deals.length === 0
    ) {
      return (
        <EmptyCard
          description={m.coInvestments_insights_lpProfile_empty_subtitle()}
          illustration={<NofilesIllustration />}
          title={m.coInvestments_insights_empty_title()}
        />
      );
    }

    return (
      <>
        <KpiMetricCards isLoading={isPending} metrics={kpiMetrics} />
        <InsightsChartCards
          chartMetrics={chartMetrics}
          dataTestId="lp-profile-charts"
          isLoading={isPending}
          pieCenterLabel={m.coInvestments_insights_lpProfile_dealsTotal()}
        />
        <FundBreakdownTable
          fundBreakdown={fundBreakdown}
          fundBreakdownTotals={fundBreakdownTotals}
          isLoading={isPending}
        />
        <DealActivityList
          deals={deals}
          isLoading={isPending}
          onViewDeal={(deal) =>
            navigate(
              generatePath(
                deal.status === InsightsDealActivityStatusEnum.COMMITTED
                  ? CoInvestmentFullPaths.CoInvestmentReport
                  : CoInvestmentFullPaths.CoInvestmentTrack,
                { id: deal.dealId },
              ),
            )
          }
        />
      </>
    );
  };

  return (
    <div className="mt-1 pb-52">
      <CoInvestmentPageHeader
        backButtonDataTestId="lp-profile-back-button"
        onBack={() => navigate(CoInvestmentFullPaths.CoInvestmentInsights)}
        showDrawer={false}
        title={limitedPartnerName}
      />
      <div className="mt-6 flex flex-col gap-12">
        {renderContent()}
        <LpVisibilitySection limitedPartnerId={limitedPartnerId} />
      </div>
    </div>
  );
};
